import React, { useCallback, useMemo } from 'react';
import DataGrid, { Action, ActionFn, SetIsLoading } from '#/common/components/DataGrid';
import { columns } from './columns';
import { actions, selectedActions } from './actions';
import { Alert, AlertState } from '#/graphql/types';
import { states } from '../../utils/constants';
import { gql } from '@apollo/client';
import { getAlertList } from '#/graphql/queries';
import { useNavigate } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import useAlertBulkUpdate from '../../hooks/useBulkUpdateAlertMutation';

const getAlertListQuery = gql(getAlertList);

const isNotNewState = (item: Alert): boolean => item.state !== states.new.value;

const AlertDataGrid: React.FC = () => {
  const navigate = useNavigate();
  const { alert, confirm } = useToast();
  const { bulkUpdateAlert } = useAlertBulkUpdate();

  const ignoreAlerts = useCallback(
    async (selectedItems: Alert[], { setIsLoading }: { setIsLoading?: SetIsLoading }) => {
      if (!selectedItems || selectedItems.length === 0) {
        return undefined;
      }

      const canIgnore = selectedItems.filter((item: Alert) => item.state === states.new.value);
      const cannotIgnore = selectedItems.filter((item: Alert) => item.state !== states.new.value);

      if (canIgnore.length === 0) {
        await alert(
          'warning',
          'None of the selected alerts can be ignored. Only alerts with "New" status can be ignored.',
        );
        return;
      }

      let confirmMessage = '';
      if (cannotIgnore.length > 0) {
        confirmMessage = `${canIgnore.length} of ${selectedItems.length} selected alerts will be ignored. Only alerts with "New" status can be ignored. Continue?`;
      } else {
        confirmMessage = `Are you sure you want to ignore ${canIgnore.length} alert${canIgnore.length > 1 ? 's' : ''}?`;
      }

      const sure = await confirm('error', confirmMessage);
      if (!sure) {
        return;
      }

      setIsLoading && setIsLoading(true, 'Ignoring alerts...');

      const idsToIgnore = canIgnore.map((item: Alert) => item.id);
      try {
        const res = await bulkUpdateAlert({
          values: { state: states.ignored.value as AlertState },
          ids: idsToIgnore,
        });
        alert('success', `${res?.updatedCount ?? 0} alerts were successfully ignored.`);
      } catch (error) {
        console.error(error);
        alert('error', 'Bulk operation has failed.');
      }
      setIsLoading && setIsLoading(false);
    },
    [bulkUpdateAlert, confirm, alert],
  );

  const userSelectedActions: ActionFn<Alert>[] = useMemo(() => {
    const res: ActionFn<Alert>[] = [];
    selectedActions.forEach((a) => {
      if (a.key === 'create-finding' || a.key === 'create-work-order') {
        res.push({
          ...a,
          disabled: (e): boolean => {
            return (
              !e.selectedItems ||
              e.selectedItems.length !== 1 ||
              (e.selectedItems.length === 1 && e.selectedItems[0].state !== states.new.value)
            );
          },
          onClick: (e): void => {
            const baseUrl = a.key === 'create-finding' ? '/findings/new' : '/work-orders/new';
            navigate(
              `${baseUrl}?&assetId=${e.selectedItems[0].assetId}&tenantId=${e.selectedItems[0].tenantId}&alertId=${e.selectedItems[0].id}`,
            );
            e.dg?.instance.clearSelection();
          },
        });
      }
      if (a.key === 'ignore') {
        res.push({
          ...a,
          disabled: (e): boolean => {
            return (
              !e.selectedItems || e.selectedItems.length < 1 || e.selectedItems.every(isNotNewState)
            );
          },
          onClick: async (e): Promise<void> => {
            await ignoreAlerts(e.selectedItems, { setIsLoading: e.setIsLoading });
            e?.setIsLoading && e.setIsLoading(false);
            e.dg?.instance.clearSelection();
            e.dg?.instance.refresh();
          },
        });
      }
    });

    return res;
  }, [selectedActions]);

  const gridActions = useMemo<Action<Alert>[]>(() => {
    return actions
      .map((a) => {
        if (typeof a === 'string') {
          return a;
        }
        const result = { ...a };

        return result;
      })
      .filter((a) => !!a);
  }, []);

  return (
    <DataGrid
      id="alerts"
      pluralText="Alerts"
      singularText="Alert"
      columns={columns}
      recordUrl="/alerts/:id"
      actions={gridActions}
      selectedActions={userSelectedActions}
      getItemsQuery={getAlertListQuery}
    />
  );
};

export default React.memo(AlertDataGrid);
